import React, { useState } from 'react';
import { Zap, ChevronDown, ChevronUp, Copy, LogIn, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * DemoUsersPanel — preview-only one-click login switcher.
 *
 * Founder ask Feb 2026: "I need to test the Demo Freelancer's dashboard
 * on preview, can you put all the user types — user id n pass — on the
 * preview so that I can check the dashboard before deploy, without
 * looking for pass/user id here n there."
 *
 * Rendered ONLY when the page host is NOT `vawworld.in` (the production
 * domain). Lists every seeded test/demo account with a one-click "Login"
 * button so the founder can hop between dashboards in seconds.
 *
 * Source of truth: /app/memory/test_credentials.md. Keep the array
 * below in sync when adding/removing seed accounts.
 */

// Curated list — pulled verbatim from /app/memory/test_credentials.md
// Group order = role hierarchy (admin → PM → client → vendor → artist).
const DEMO_USERS = [
  // ── Admin / Production ───────────────────────────────────────────
  {
    group: 'Admin / PM',
    label: 'Master Admin · KumarSammbhav',
    email: 'kumar@vawworld.tv', password: 'admin123',
    role: 'master_admin',
    note: 'Full access · BOTS · HR · NAS · Maintenance Tree',
    accent: 'rose',
  },
  {
    group: 'Admin / PM',
    label: 'Production Manager · Pushpa',
    email: 'pushpa@vawworld.tv', password: 'pushpa123',
    role: 'production_manager',
    note: 'Scoped to "Fairy Tales Collection" project',
    accent: 'amber',
  },
  // ── Client (the Approvals / Timeline experience) ─────────────────
  {
    group: 'Client',
    label: 'Client · Chloë Mattei (Animaj)',
    email: 'chloe@animaj.com', password: 'Chloe@2026',
    role: 'client_producer',
    note: 'FairyTales / Hansel & Gretel / Little Mermaid / Thumbelina',
    accent: 'cyan',
  },
  {
    group: 'Client',
    label: 'Client · Saran Kumar (Animaj)',
    email: 'saran@animaj.com', password: 'Saran@2026',
    role: 'client_producer',
    note: 'Same project scope as Chloë',
    accent: 'cyan',
  },
  {
    group: 'Client',
    label: 'Client · Priya Menon (TSM seed)',
    email: 'client@vawworld.tv', password: 'client123',
    role: 'client_producer',
    note: '3 seeded demo submissions waiting for approval',
    accent: 'cyan',
  },
  // ── Employee / Artist ────────────────────────────────────────────
  {
    group: 'Employee / Artist',
    label: 'Demo Employee (clean)',
    email: 'demo.employee@vawworld.tv', password: 'Employee@2026',
    role: 'animator · employee',
    note: 'Fresh — no tasks/payslips/leaves seeded',
    accent: 'emerald',
  },
  {
    group: 'Employee / Artist',
    label: 'Modeler 3D · Khojendra (heavy)',
    email: 'khojendra@gmail.com', password: 'Khojendra@2026',
    role: 'modeler_3d · employee',
    note: '224 active tasks · realistic load test',
    accent: 'emerald',
  },
  {
    group: 'Employee / Artist',
    label: 'Test Employee · Riya (HR/payslip)',
    email: 'riya.test@vawworld.tv', password: 'kF6W2prKzXVP',
    role: 'animator · employee',
    note: 'Has full CTC + payslip + leaves seed (must_change_password)',
    accent: 'emerald',
  },
  // ── Vendor 3-tier hierarchy ──────────────────────────────────────
  {
    group: 'Vendor Hierarchy',
    label: 'Vendor Producer · L1',
    email: 'vp.test@vendor.com', password: 'VpTest@2026',
    role: 'vendor_producer',
    note: 'Top-of-tree vendor org — WO + invite cascade',
    accent: 'violet',
  },
  {
    group: 'Vendor Hierarchy',
    label: 'Vendor Team Manager · L2',
    email: 'l2.mgr@vendor.com', password: 'L2@2026',
    role: 'vendor_team_manager',
    note: 'Parent: vp.test',
    accent: 'violet',
  },
  {
    group: 'Vendor Hierarchy',
    label: 'Vendor Team Artist · L3',
    email: 'l3.artist2@vendor.com', password: 'L3@2026',
    role: 'vendor_artist',
    note: 'Bottom-of-tree, sees My Tasks like a freelancer',
    accent: 'violet',
  },
  // ── Legacy / Smoke ───────────────────────────────────────────────
  {
    group: 'Vendor Hierarchy',
    label: 'Vendor Producer (commercial seed)',
    email: 'vos-vendor-1777666468@example.com', password: 'vendor123',
    role: 'vendor_producer',
    note: 'WO / Vendor Projects / invite testing',
    accent: 'violet',
  },
];

// Tailwind accent palette — keep these as full class strings (Tailwind
// JIT can't see dynamic class names like `bg-${accent}-500/15`).
const ACCENT_CLS = {
  rose:    'bg-rose-500/10 border-rose-500/30 text-rose-200 hover:bg-rose-500/20',
  amber:   'bg-amber-500/10 border-amber-500/30 text-amber-200 hover:bg-amber-500/20',
  cyan:    'bg-cyan-500/10 border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/20',
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/20',
  violet:  'bg-violet-500/10 border-violet-500/30 text-violet-200 hover:bg-violet-500/20',
};

export default function DemoUsersPanel() {
  const { login } = useAuth();
  const [expanded, setExpanded] = useState(true);  // open by default on preview
  const [busy, setBusy] = useState(null);  // email of the row currently logging in

  // PROD GUARD — never render on the production domain (vawworld.in).
  // We also accept a manual ?demo=1 override on prod just in case the
  // founder wants to bypass without changing code, but default is OFF.
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    const isProd = host === 'vawworld.in' || host.endsWith('.vawworld.in');
    const forceShow = new URLSearchParams(window.location.search).get('demo') === '1';
    if (isProd && !forceShow) return null;
  }

  const quickLogin = async (u) => {
    if (busy) return;
    setBusy(u.email);
    try {
      await login(u.email, u.password);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Login failed for ${u.email}`);
    } finally {
      setBusy(null);
    }
  };

  const copyCreds = (u) => {
    const text = `${u.email} / ${u.password}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
    }
  };

  // Group accounts for the rendered list
  const grouped = DEMO_USERS.reduce((acc, u) => {
    if (!acc[u.group]) acc[u.group] = [];
    acc[u.group].push(u);
    return acc;
  }, {});

  return (
    <div className="mt-4 bg-zinc-900/60 border border-amber-500/30 rounded-sm overflow-hidden" data-testid="demo-users-panel">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-zinc-900/80 transition-colors"
        data-testid="demo-users-toggle"
      >
        <div className="flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-[11px] uppercase tracking-wider text-amber-300 font-bold">Preview · Demo Users</span>
          <span className="text-[9px] text-zinc-500 normal-case">one-click login · {DEMO_USERS.length} accounts</span>
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          <p className="text-[9px] text-zinc-600 italic">
            This panel is hidden on production (vawworld.in). Source: <code className="text-zinc-500">/app/memory/test_credentials.md</code>.
          </p>
          {Object.entries(grouped).map(([groupName, users]) => (
            <div key={groupName}>
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold mb-1 flex items-center gap-1.5">
                <Users className="w-2.5 h-2.5" /> {groupName}
              </p>
              <div className="space-y-1.5">
                {users.map(u => {
                  const cls = ACCENT_CLS[u.accent] || ACCENT_CLS.cyan;
                  const isBusy = busy === u.email;
                  return (
                    <div key={u.email}
                      className={`rounded border px-2.5 py-1.5 flex items-center gap-2 transition-colors ${cls}`}
                      data-testid={`demo-user-${u.email}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-medium text-white truncate">{u.label}</p>
                        <p className="text-[9px] text-zinc-400 truncate font-mono" title={`${u.email} / ${u.password}`}>
                          {u.email} <span className="opacity-50">· {u.role}</span>
                        </p>
                        {u.note && (
                          <p className="text-[9px] text-zinc-500 italic truncate">{u.note}</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyCreds(u); }}
                        title="Copy email / password"
                        data-testid={`demo-user-copy-${u.email}`}
                        className="text-zinc-500 hover:text-white p-1 rounded hover:bg-zinc-950/40 flex-shrink-0"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => quickLogin(u)}
                        disabled={isBusy || !!busy}
                        data-testid={`demo-user-login-${u.email}`}
                        className="flex-shrink-0 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-zinc-950/60 hover:bg-zinc-950 text-white border border-current rounded flex items-center gap-1 disabled:opacity-40"
                      >
                        <LogIn className="w-2.5 h-2.5" />
                        {isBusy ? '...' : 'Login'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
